import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import type { CSSProperties } from "react";
import {
    useEmissionRecordsApiClient,
    useNondetectsApiClient,
} from "../../../../../../hooks";
import { formatDateTime } from "../../../../../../utils/dates";
import type { PlotEmissionMark } from "./marks";

const Loading = () => (
    <div className="w-full h-full p-1 flex items-center justify-center">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
    </div>
);

const GroupHover = ({
    mark,
}: {
    mark: Extract<PlotEmissionMark, { kind: "groupedEmissions" }>;
}) => (
    <div>
        <p className="font-bold mb-2">
            {mark.records[0].providerName} (grouped)
        </p>
        <p className="mb-1">{formatDateTime(mark.x)}</p>
        <p className="mb-1">
            Sum: {(mark.y / 1000).toFixed(2)}
            {" kg/h"}
        </p>
        <p className="mb-1">{mark.records.length} emissions grouped.</p>
    </div>
);

const EmissionHover = ({
    emissionId,
    averageWindow,
}: {
    emissionId: string;
    // Set for providers reporting averages: replaces the detection timestamp.
    averageWindow?: { start: Date; end: Date };
}) => {
    const emissionApiClient = useEmissionRecordsApiClient();
    const emissionQuery = useQuery({
        queryKey: ["emissionRecord", emissionId],
        queryFn: async () => {
            const response = await emissionApiClient.emissionRecordsRetrieve({
                id: emissionId,
            });
            return response;
        },
        refetchOnWindowFocus: false,
        retry: false,
    });

    if (emissionQuery.isLoading || !emissionQuery.data) {
        return <Loading />;
    }

    return (
        <div>
            <p className="font-bold mb-2">{emissionQuery.data.providerName}</p>
            {averageWindow ? (
                <>
                    <p className="mb-1 rounded bg-yellow-100 px-1 text-yellow-900">
                        24-hour average
                    </p>
                    <p className="mb-1">
                        {formatDateTime(averageWindow.start)}
                        {" – "}
                        {formatDateTime(averageWindow.end)}
                    </p>
                </>
            ) : (
                <p className="mb-1">
                    {formatDateTime(
                        emissionQuery.data.dataPoint.detectionTimestamp,
                    )}
                </p>
            )}
            {emissionQuery.data.dataPoint.secondaryDataSource && (
                <p className="mb-1">
                    {emissionQuery.data.dataPoint.secondaryDataSource}
                </p>
            )}
            {emissionQuery.data.dataPoint.detectedRate && (
                <p className="mb-1">
                    {(emissionQuery.data.dataPoint.detectedRate / 1000).toFixed(
                        2,
                    )}
                    {" ± "}
                    {(
                        (emissionQuery.data.dataPoint.detectedRateUncertainty ??
                            0) / 1000
                    ).toFixed(2)}
                    {" kg/h"}
                </p>
            )}
            {!emissionQuery.data.dataPoint.detectedRate &&
                emissionQuery.data.dataPoint.concentration && (
                    <p className="mb-1">
                        {emissionQuery.data.dataPoint.concentration.toFixed(2)}
                        {" ± "}
                        {emissionQuery.data.dataPoint.concentrationUncertainty?.toFixed(
                            2,
                        )}
                        {" ppm*m"}
                    </p>
                )}
            {emissionQuery.data.dataPoint.data?.bridger_equipment_type && (
                <p className="mb-1">
                    {emissionQuery.data.dataPoint.data?.bridger_equipment_type}
                </p>
            )}
        </div>
    );
};

const NonDetectHover = ({ nonDetectId }: { nonDetectId: string }) => {
    const nonDetectsApiClient = useNondetectsApiClient();
    const nonDetectQuery = useQuery({
        queryKey: ["nondetect", nonDetectId],
        queryFn: async () => {
            const response =
                await nonDetectsApiClient.nondetectsSitenondetectRetrieve({
                    id: nonDetectId,
                });
            return response;
        },
        enabled: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    if (nonDetectQuery.isLoading || !nonDetectQuery.data) {
        return <Loading />;
    }

    return (
        <div>
            <p className="font-bold">{nonDetectQuery.data.dataProvider.name}</p>
            <p className="font-bold mb-1">(Non Detect Observation)</p>
            <p className="mb-1">
                {formatDateTime(nonDetectQuery.data.timestamp)}
            </p>
            {nonDetectQuery.data.secondaryDataSource && (
                <p className="mb-1">
                    {nonDetectQuery.data.secondaryDataSource}
                </p>
            )}
            {nonDetectQuery.data.detectionLimit !== null && (
                <p className="mb-1">
                    {(nonDetectQuery.data.detectionLimit / 1000).toFixed(2)}
                    {" kg/h"}
                </p>
            )}
        </div>
    );
};

const MarkHover = ({ mark }: { mark: PlotEmissionMark }) => {
    switch (mark.kind) {
        case "groupedEmissions":
            return <GroupHover mark={mark} />;
        case "dailyAverage":
            return (
                <EmissionHover
                    emissionId={mark.record.id}
                    averageWindow={{ start: mark.x, end: mark.xEnd }}
                />
            );
        case "point":
        case "concentration":
            return <EmissionHover emissionId={mark.record.id} />;
    }
};

export const EmissionPlotHover = ({
    mark,
    nonDetectId,
    style,
}: {
    mark?: PlotEmissionMark;
    nonDetectId?: string;
    style?: CSSProperties;
}) => {
    if (!mark && !nonDetectId) {
        return null;
    }

    return (
        <div
            className="bg-white rounded drop-shadow border p-1 text-xs"
            style={style}
        >
            {mark && <MarkHover mark={mark} />}
            {nonDetectId && <NonDetectHover nonDetectId={nonDetectId} />}
        </div>
    );
};
